import React, { useRef, useState } from "react";
import styled from "styled-components";
import {
  IonContent,
  IonPage,
  useIonViewDidEnter,
  useIonViewDidLeave,
} from "@ionic/react";
import { useParams } from "react-router-dom";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import { useVideoContextWindow } from "../../hooks/useVideoContextWindow";
import { VideoSwiper } from "../../components/atoms/VideoSwiper";
import { VideoSlide } from "../../components/atoms/VideoSlide";
import { VideoMeta } from "../../services/video.service";

const PageWrapper = styled.div`
  position: relative;
  height: 100vh;
  background: #000;
`;
const TopBar = styled.div`
  position: fixed;
  top: 20px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  padding: 0 20px;
  z-index: 20;
`;
const TopButton = styled.button`
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  font-size: 16px;
  padding: 10px 14px;
  border-radius: 999px;
  cursor: pointer;
`;

const shareUrlFrom = (hlsUrl: string) => {
  try {
    const u = new URL(hlsUrl);
    const parts = u.pathname.split("/").filter(Boolean);
    const uid = parts[0];
    return `${u.origin}/${uid}/watch`;
  } catch {
    return hlsUrl;
  }
};

const DisplayVideo: React.FC = () => {
  const [pageActive, setPageActive] = useState(false);
  const { id } = useParams<{ id: string }>();

  const search = window.location.search;
  const searchParams = new URLSearchParams(search);
  const tags =
    searchParams.get("tags")?.split(",").map((t) => t.trim()).filter(Boolean) ?? [];

  const { setResonanceLayout } = useResonanceLayout();

  const {
    videos,
    currentIndex,
    setCurrentIndex,
    loadPrev,
    loadNext,
    initialSlide,
    swiperKey,
    initSwiperDoneRef,
    suppressEdgeLoadsRef,
  } = useVideoContextWindow(id, tags);

  const [liked, setLiked] = useState(false);
  const [shared, setShared] = useState(false);
  const [muted, setMuted] = useState(true);

  const wantPlayRef = useRef(false);

  useIonViewDidEnter(() => {
    setPageActive(true);
    wantPlayRef.current = true;
  });

  useIonViewDidLeave(() => {
    setPageActive(false);
    wantPlayRef.current = false;
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      welcomePage: false,
      title: "Accueil",
    });
  });

  return (
    <IonPage>
      <IonContent scrollY={false} fullscreen>
        <PageWrapper>
          <TopBar>
            <TopButton onClick={() => history.back()}>← Retour</TopButton>
            <TopButton onClick={() => history.back()}>Tout voir</TopButton>
          </TopBar>

          <VideoSwiper<VideoMeta>
            items={videos}
            index={currentIndex}
            setIndex={(i) => {
              setCurrentIndex(i);
              const nextId = videos[i]?.uid;
              if (nextId && id && nextId !== id) {
                const url = new URL(window.location.href);
                const parts = url.pathname.split("/").filter(Boolean);
                if (parts.length >= 2) {
                  parts[parts.length - 2] = nextId;
                }
                const newPath = "/" + parts.join("/");
                const newUrl = newPath + url.search;
                window.history.replaceState(null, "", newUrl);
              }
            }}
            initialSlide={initialSlide}
            remountKey={swiperKey}
            onNearStart={loadPrev}
            onNearEnd={loadNext}
            initDoneRef={initSwiperDoneRef}
            suppressEdgeLoadsRef={suppressEdgeLoadsRef}
            renderItem={(vid, i) => (
              <VideoSlide
                src={vid.hlsUrl}
                thumbnail={vid.thumbnailUrl}
                active={pageActive && i === currentIndex}
                shouldPlay={pageActive && i === currentIndex}
                muted={muted}
                liked={liked}
                shared={shared}
                onToggleLike={() => setLiked((v) => !v)}
                onShare={async () => {
                  const url = shareUrlFrom(vid.hlsUrl);
                  await navigator.clipboard.writeText(url);
                  setShared(true);
                  setTimeout(() => setShared(false), 1200);
                }}
                onToggleMute={() => setMuted((m) => !m)}
              />
            )}
          />
        </PageWrapper>
      </IonContent>
    </IonPage>
  );
};

export default DisplayVideo;
